import React, { useEffect, useState } from "react";
import {
  User,
  Mail,
  Phone as PhoneIcon,
  MapPin,
  Crown,
  BarChart2,
  Trash2,
  AlertTriangle,
} from "lucide-react";

import { db } from "./firebase"; // <-- ensure db is exported from your firebase init
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { apiCall } from './utils/api';

// ---- Delete function (calls FastAPI proxy route) ----
async function deleteUser(uid) {
  try {
    const { data } = await apiCall(
      `/api/delete-user/${encodeURIComponent(uid)}`,
      { method: "DELETE", credentials: "include" }
    );
    alert(`✅ User deleted successfully: ${data.uid || uid}`);
    return data;
  } catch (error) {
    console.error("Error deleting user:", error);
    alert(`❌ ${error.message}`);
    throw error;
  }
}

// Small helper row: show full value, allow wrapping (no truncation)
function InfoRow({ icon: Icon, label, value, title, right }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="text-gray-300 mt-0.5 shrink-0" size={16} />
      <div className="min-w-0 flex-1">
        <p className="text-gray-400 text-xs">{label}</p>
        <p className="text-white break-words">
          <span className="block whitespace-normal break-words" title={title || value}>
            <span className="break-all">{value}</span>
          </span>
        </p>
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>
  );
}

export default function UserProfile({ user, userData, resumeData }) {
  const contact = resumeData?.personalInfo || {};
  const name = user?.displayName || contact?.name || "—";
  const email = user?.email || contact?.email || "—";
  const location = contact?.location || "—";
  const planTierRaw = userData?.role ? `${userData.role} Plan` : "Free Plan";

  // Usage state
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState(null);

  // Delete state
  const [deleting, setDeleting] = useState(false);

  // Phone state (Option A: Firestore)
  const [phoneValue, setPhoneValue] = useState("");      // what we show
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");      // edit buffer
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneMsg, setPhoneMsg] = useState(null);
  const [phoneErr, setPhoneErr] = useState(null);
  const phonePresent = !!(phoneValue && phoneValue.trim());

  // Load usage
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        setUsageLoading(true);
        const { data } = await apiCall('/api/usage/me');
        if (!cancelled) setUsage(data || {});
      } catch {
        if (!cancelled) setUsageError("Could not load usage.");
      } finally {
        if (!cancelled) setUsageLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  // 🔹 Load phone from Firestore (authoritative for Option A)
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const fsPhone = snap.exists() ? (snap.data()?.phone || "") : "";
        if (!cancelled) {
          // fallbacks if Firestore empty
          const fallback =
            fsPhone ||
            userData?.phone ||
            contact?.phone ||
            user?.phoneNumber || "";
          setPhoneValue(fallback);
        }
      } catch (e) {
        console.warn("Failed to load phone from Firestore:", e);
        // still show fallback if FS fails
        const fallback = userData?.phone || contact?.phone || user?.phoneNumber || "";
        setPhoneValue(fallback);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]); // eslint-disable-line

  // Build usage rows (in/out only)
  const usageRows = Object.entries(usage || {})
    .filter(([k]) => k !== "updated_at")
    .map(([service, v]) => {
      const inTok = v?.in_tokens ?? v?.inTokens ?? 0;
      const outTok = v?.out_tokens ?? v?.outTokens ?? 0;
      const kind = v?.in_tokens != null || v?.out_tokens != null ? "exact" : "estimated";
      return { service, inTok, outTok, kind };
    })
    .sort((a, b) => b.inTok + b.outTok - (a.inTok + a.outTok));

  const monthlyInTotal = usageRows.reduce((acc, r) => acc + (r.inTok || 0), 0);
  const monthlyOutTotal = usageRows.reduce((acc, r) => acc + (r.outTok || 0), 0);

  // Save phone to Firestore
  const handleSavePhone = async () => {
    setPhoneErr(null);
    setPhoneMsg(null);
    const raw = (phoneInput || "").trim();
    const isValid = /^\+?[0-9\s-]{8,20}$/.test(raw);
    if (!isValid) {
      setPhoneErr("Please enter a valid phone number (e.g., +91 9876543210).");
      return;
    }
    if (!user?.uid) {
      setPhoneErr("You must be logged in.");
      return;
    }
    try {
      setPhoneSaving(true);
      await setDoc(
        doc(db, "users", user.uid),
        {
          uid: user.uid,
          phone: raw,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setPhoneValue(raw);
      setPhoneMsg("Phone number saved.");
      setEditingPhone(false);
    } catch (e) {
      console.error(e);
      setPhoneErr("Failed to save phone number. Please try again.");
    } finally {
      setPhoneSaving(false);
    }
  };

  // Delete handler
  const handleDeleteAccount = async () => {
    if (!user?.uid) return alert("User not logged in.");
    const confirmDelete = window.confirm(
      "⚠️ This will permanently delete your account and all your data.\n\nAre you absolutely sure?"
    );
    if (!confirmDelete) return;
    try {
      setDeleting(true);
      await deleteUser(user.uid);
      window.location.href = "/";
    } catch {
      // surfaced
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <header className="mb-8">
        <h2 className="text-3xl font-bold text-white">User Details</h2>
        <p className="text-gray-400 mt-1">
          Manage your profile information, plan, and account settings.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Profile Info */}
        <div className="bg-[#3C0F6C] p-6 rounded-xl border border-gray-700 shadow-sm">
          <div className="flex items-center gap-4 min-w-0">
            <img
              src={user?.photoURL || "https://placehold.co/80x80/3C0F6C/FFFFFF?text=U"}
              alt="Avatar"
              className="w-16 h-16 rounded-full border border-gray-600 object-cover shrink-0"
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <User size={18} className="text-yellow-400 shrink-0" />
                <h3 className="text-xl font-semibold text-white" title={name}>
                  <span className="break-words">{name}</span>
                </h3>
              </div>
              <div
                className="mt-1 inline-flex items-center gap-2 text-xs font-medium text-yellow-300 bg-yellow-500/10 px-2 py-0.5 rounded-full"
                title={planTierRaw}
              >
                <Crown size={14} className="shrink-0" />
                <span className="break-words">{planTierRaw}</span>
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-3 text-sm">
            <InfoRow icon={Mail} label="Email" value={email} title={email} />

            {/* Phone row (Option A) */}
            {!phonePresent && !editingPhone && (
              <InfoRow
                icon={PhoneIcon}
                label="Phone"
                value="— not added —"
                right={
                  <button
                    onClick={() => {
                      setPhoneInput("");
                      setPhoneErr(null);
                      setPhoneMsg(null);
                      setEditingPhone(true);
                    }}
                    className="px-2 py-1 text-xs rounded-md bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30"
                  >
                    Add phone
                  </button>
                }
              />
            )}

            {editingPhone && (
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <PhoneIcon className="text-gray-300 mt-0.5 shrink-0" size={16} />
                  <div className="flex-1">
                    <label className="text-gray-400 text-xs">Phone</label>
                    <input
                      type="tel"
                      placeholder="e.g., +91 9876543210"
                      className="mt-1 w-full bg-[#4a3c72] text-white px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      value={phoneInput}
                      onChange={(e) => setPhoneInput(e.target.value)}
                      inputMode="tel"
                      autoComplete="tel"
                    />
                    {phoneErr && <p className="text-red-300 text-xs mt-1">❌ {phoneErr}</p>}
                    {phoneMsg && <p className="text-green-300 text-xs mt-1">✅ {phoneMsg}</p>}
                    <div className="mt-2 flex gap-2">
                      <button
                        onClick={handleSavePhone}
                        disabled={phoneSaving}
                        className={`px-3 py-1.5 rounded-md text-sm font-semibold ${
                          phoneSaving
                            ? "bg-yellow-700 text-white cursor-not-allowed"
                            : "bg-yellow-500 text-gray-900 hover:bg-yellow-600"
                        }`}
                      >
                        {phoneSaving ? "Saving…" : "Save"}
                      </button>
                      <button
                        onClick={() => {
                          setEditingPhone(false);
                          setPhoneErr(null);
                          setPhoneMsg(null);
                        }}
                        className="px-3 py-1.5 rounded-md text-sm bg-gray-700 text-gray-100 hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {phonePresent && (
              <InfoRow
                icon={PhoneIcon}
                label="Phone"
                value={phoneValue}
                title={phoneValue}
                right={
                  <button
                    onClick={() => {
                      setPhoneInput(phoneValue);
                      setPhoneErr(null);
                      setPhoneMsg(null);
                      setEditingPhone(true);
                    }}
                    className="px-2 py-1 text-xs rounded-md bg-gray-700 text-gray-100 hover:bg-gray-600"
                  >
                    Edit
                  </button>
                }
              />
            )}

            <InfoRow icon={MapPin} label="Location" value={location} title={location} />
          </div>
        </div>

        {/* Right: Plan + Usage + Danger Zone */}
        <div className="lg:col-span-2 space-y-8">
          {/* Plan Info */}
          <div className="bg-[#3C0F6C] p-6 rounded-xl border border-gray-700 shadow-sm">
            <h3 className="text-xl font-bold text-white mb-4">Plan</h3>
            <div className="p-4 rounded-lg bg-gray-800 border border-gray-700">
              <p className="text-gray-300">
                You’re currently on{" "}
                <span className="font-semibold text-white align-middle" title={planTierRaw}>
                  {planTierRaw}
                </span>
                .
              </p>
              <p className="text-gray-400 mt-2 text-sm">
                Enjoy full access to Resume Builder, Cover Letters, Resume
                Scoring, Job Matching, and Interview Prep features (subject to your plan limits).
              </p>
              <button
                className="mt-4 px-4 py-2 rounded-lg bg-yellow-500 text-gray-900 font-semibold hover:bg-yellow-600 transition-colors"
                onClick={() => window.location.href = "https://job.novistep.com/dashboard/pricing"}
              >
                Manage Plan
              </button>
            </div>
          </div>

          {/* AI Usage */}
          <div className="bg-[#3C0F6C] p-6 rounded-xl border border-gray-700 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <BarChart2 className="text-yellow-400" size={18} />
              <h3 className="text-xl font-bold text-white">This Month’s AI Usage</h3>
            </div>

            {usageLoading ? (
              <p className="text-gray-300 text-sm">Loading usage…</p>
            ) : usageError ? (
              <p className="text-red-300 text-sm">{usageError}</p>
            ) : usageRows.length === 0 ? (
              <p className="text-gray-300 text-sm">No usage recorded yet.</p>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-gray-800 border border-gray-700">
                  <p className="text-gray-300 text-sm">Monthly input tokens</p>
                  <p className="text-yellow-300 font-semibold text-base tabular-nums">
                    {monthlyInTotal} tokens
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-gray-800 border border-gray-700">
                  <p className="text-gray-300 text-sm">Monthly output tokens</p>
                  <p className="text-yellow-300 font-semibold text-base tabular-nums">
                    {monthlyOutTotal} tokens
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Danger Zone */}
          <div className="bg-[#3C0F6C] p-6 rounded-xl border border-red-700/60 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="text-red-400" size={18} />
              <h3 className="text-xl font-bold text-red-200">Danger Zone</h3>
            </div>
            <p className="text-gray-300 text-sm">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            <button
              onClick={handleDeleteAccount}
              disabled={deleting}
              className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
                deleting ? "bg-red-700 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"
              } text-white`}
            >
              <Trash2 size={16} />
              {deleting ? "Deleting…" : "Delete My Account"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
